import React, { useEffect, useState, useRef, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, Star, ShieldCheck, Clock, TrendingUp, Users, BookOpen, Gift, ChevronDown, ChevronRight, ChevronLeft, AlertTriangle, Target, Zap, XCircle } from 'lucide-react';
import { initMetaPixel } from './utils/metaPixel';
const MOCKUP_URL = 'https://cdn.magicpatterns.com/uploads/bxNGEkg1tQbf5nujr8k9KX/image-Photoroom_(1)_(1).png';
const VIDEO_URL = 'https://cdn.magicpatterns.com/uploads/mE3Hj2f3TgJ313SvuTxsH4/screenrecording kickboxing resuelto.mov';
const LINK_NORMAL = 'https://checkout.academiaonlinepro.shop/cart/47416313217209:1';
const LINK_PRO = 'https://checkout.academiaonlinepro.shop/cart/47416415781049:1';
// --- Shared Components ---
const ScrollButton = ({
  children,
  className = '',
  text = 'Comprar ahora'
}: {
  children?: React.ReactNode;
  className?: string;
  text?: string;
}) => {
  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    document.getElementById('pricing-top')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  return <button onClick={handleClick} className={`inline-flex items-center justify-center gap-2 bg-gradient-to-r from-[#E53935] to-[#b71c1c] text-[#F5F5F5] font-display font-bold py-4 px-8 rounded-full shadow-[0_0_15px_rgba(229,57,53,0.4)] hover:shadow-[0_0_25px_rgba(229,57,53,0.6)] hover:scale-105 transition-all duration-300 ${className}`} data-id="element-0">
      {children || text}
    </button>;
};
const PremiumCard = ({
  children,
  className = ''
}: {
  children: React.ReactNode;
  className?: string;
}) => <div className={`bg-[#1F1F1F] rounded-[24px] border border-[#E53935]/30 shadow-[0_0_15px_rgba(212,175,55,0.05)] hover:-translate-y-1 hover:shadow-[0_0_25px_rgba(212,175,55,0.15)] hover:border-[#E53935]/60 transition-all duration-300 overflow-hidden ${className}`} data-id="element-1">
    {children}
  </div>;
const Carousel = ({
  items,
  renderItem
}: {
  items: any[];
  renderItem: (item: any, index: number) => React.ReactNode;
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const scroll = (dir: 'left' | 'right') => {
    if (scrollRef.current) {
      const scrollAmount = scrollRef.current.clientWidth * 0.8;
      scrollRef.current.scrollBy({
        left: dir === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };
  const handleScroll = () => {
    if (scrollRef.current) {
      const index = Math.round(scrollRef.current.scrollLeft / scrollRef.current.clientWidth);
      setActiveIndex(index);
    }
  };
  return <div className="relative w-full group" data-id="element-2">
      <button onClick={() => scroll('left')} className="absolute left-2 top-1/2 -translate-y-1/2 z-10 bg-[#171717]/80 backdrop-blur-sm border border-[#E53935]/50 rounded-full p-2 text-[#F5F5F5] hover:text-[#D4AF37] hover:bg-[#1F1F1F] transition-colors opacity-0 group-hover:opacity-100 disabled:opacity-0" disabled={activeIndex === 0} data-id="element-3">
        <ChevronLeft className="w-6 h-6" data-id="element-4" />
      </button>

      <div ref={scrollRef} onScroll={handleScroll} className="flex overflow-x-auto gap-4 pb-8 pt-4 px-4 no-scrollbar snap-x snap-mandatory" data-id="element-5">
        {items.map((item, i) => <div key={i} className="snap-start flex-shrink-0 w-[80vw] md:w-[calc(33.333%-1rem)]" data-id="element-6">
            {renderItem(item, i)}
          </div>)}
      </div>

      <button onClick={() => scroll('right')} className="absolute right-2 top-1/2 -translate-y-1/2 z-10 bg-[#171717]/80 backdrop-blur-sm border border-[#E53935]/50 rounded-full p-2 text-[#F5F5F5] hover:text-[#D4AF37] hover:bg-[#1F1F1F] transition-colors opacity-0 group-hover:opacity-100 disabled:opacity-0" disabled={activeIndex >= items.length - 1} data-id="element-7">
        <ChevronRight className="w-6 h-6" data-id="element-8" />
      </button>

      <div className="flex justify-center gap-2 mt-2" data-id="element-9">
        {items.map((_, i) => <div key={i} className={`h-1.5 rounded-full transition-all duration-300 ${i === activeIndex ? 'w-6 bg-[#E53935]' : 'w-2 bg-[#1F1F1F] border border-[#E53935]/30'}`} data-id="element-10" />)}
      </div>
    </div>;
};
// --- Main App ---
export function App() {
  useEffect(() => {
    initMetaPixel();
  }, []);
  return <div className="min-h-screen bg-diagonal-lines text-[#F5F5F5] font-sans selection:bg-[#E53935] selection:text-white relative" data-id="element-11">
      <Hero data-id="element-12" />
      <SocialProof data-id="element-13" />
      <ExploreInterior data-id="element-14" />
      <PainPoints data-id="element-15" />
      <TargetAudience data-id="element-16" />
      <ProductOverview data-id="element-17" />
      <TomosSection data-id="element-18" />
      <BonusesSection data-id="element-19" />
      <GuaranteeBanner data-id="element-20" />
      <CostOfNotHaving data-id="element-21" />
      <PricingSection id="pricing-top" data-id="element-22" />
      <UpsellDetails data-id="element-23" />
      <GuaranteeBanner data-id="element-24" />
      <FAQSection data-id="element-25" />
      <UrgencySection data-id="element-26" />
      <PricingSection id="pricing-bottom" data-id="element-27" />
      <Footer data-id="element-28" />
      <RecentPurchasePopup data-id="element-29" />
    </div>;
}
// --- Sections ---
function Hero() {
  return <section className="relative pt-8 pb-16 md:pt-20 md:pb-24 px-4 overflow-hidden flex flex-col items-center text-center" data-id="element-30">
      {/* Background Effects */}
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-[#E53935] opacity-10 blur-[150px] rounded-full pointer-events-none animate-pulse-glow" data-id="element-31" />
      <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[400px] h-[400px] bg-[#D4AF37] opacity-5 blur-[100px] rounded-full pointer-events-none" data-id="element-32" />

      {/* Particles */}
      {[...Array(10)].map((_, i) => <div key={i} className="absolute w-1 h-1 bg-[#E53935] rounded-full animate-float opacity-30" style={{
      left: `${Math.random() * 100}%`,
      top: `${Math.random() * 100}%`,
      animationDelay: `${Math.random() * 5}s`,
      animationDuration: `${3 + Math.random() * 4}s`
    }} data-id="element-33" />)}

      <div className="relative z-10 w-full max-w-4xl mx-auto flex flex-col items-center" data-id="element-34">
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#1F1F1F] border border-[#E53935]/30 text-xs md:text-sm font-medium text-[#D4AF37] mb-6 shadow-[0_0_10px_rgba(212,175,55,0.1)]" data-id="element-35">
          <span className="flex h-2 w-2 rounded-full bg-[#E53935] animate-pulse" data-id="element-36"></span>
          Sistema N°1 para Instructores de Kickboxing
        </div>

        <h1 className="text-4xl md:text-6xl lg:text-7xl font-display font-black tracking-tight mb-6 leading-[1.1] text-[#F5F5F5] drop-shadow-lg" data-id="element-37">
          MegaPack Kickboxing Resuelto
        </h1>

        <motion.div className="w-[70%] max-w-[280px] md:max-w-[450px] mx-auto mb-6 relative" animate={{
        y: [0, -15, 0]
      }} transition={{
        duration: 5,
        repeat: Infinity,
        ease: 'easeInOut'
      }} data-id="element-38">
          <div className="absolute inset-0 bg-[#D4AF37] opacity-10 blur-3xl rounded-full" data-id="element-39" />
          <img src={MOCKUP_URL} alt="MegaPack Kickboxing Resuelto" className="w-full h-auto object-contain relative z-10 drop-shadow-[0_20px_30px_rgba(0,0,0,0.8)]" data-id="element-40" />
        </motion.div>

        <p className="text-lg md:text-2xl text-gray-300 mb-8 max-w-2xl mx-auto font-medium leading-relaxed" data-id="element-41">
          <span className="text-[#D4AF37] font-bold" data-id="element-42">
            6 Tomos + 6 Bonos — PDF Digital · Acceso Inmediato
          </span>
          <br data-id="element-43" />
          Clases listas, variadas y profesionales durante todo un año
        </p>

        <ScrollButton text="💥 QUIERO MI MEGAPACK AHORA" className="w-[90%] md:w-auto text-lg md:text-xl" data-id="element-44" />

        <div className="flex flex-wrap justify-center items-center gap-4 md:gap-8 mt-8 text-sm text-gray-400 font-medium bg-[#1F1F1F]/50 py-3 px-6 rounded-full border border-white/5 backdrop-blur-sm" data-id="element-45">
          <div className="flex items-center gap-1.5" data-id="element-46">
            <ShieldCheck className="w-5 h-5 text-[#D4AF37]" data-id="element-47" />
            Garantía 7 días
          </div>
          <div className="hidden md:block w-1 h-1 rounded-full bg-gray-600" data-id="element-48" />
          <div className="flex items-center gap-1.5" data-id="element-49">
            <Users className="w-5 h-5 text-[#E53935]" data-id="element-50" />
            +1000 Instructores
          </div>
          <div className="hidden md:block w-1 h-1 rounded-full bg-gray-600" data-id="element-51" />
          <div className="flex items-center gap-1.5" data-id="element-52">
            <Star className="w-5 h-5 text-[#D4AF37]" fill="currentColor" data-id="element-53" />
            4.8 Rating
          </div>
        </div>
      </div>
    </section>;
}
function SocialProof() {
  const testimonials = [{
    name: 'Micaela N.',
    role: 'Instructora · Buenos Aires',
    text: 'Antes me la pasaba horas buscando qué dar el lunes, ahora en 5 minutos elijo clase y dedicó toda mi energía a enseñar. Mis alumnos notaron la diferencia y las clases crecen cada semana.'
  }, {
    name: 'Matías R.',
    role: 'Entrenador Kickboxing · Córdoba',
    text: 'La estructura me devolvió la confianza. No improviso más y eso se refleja en la retención de alumnos. Puedo planificar todo el mes de una sola vez.'
  }, {
    name: 'Javier S.',
    role: 'Profesor · Santiago',
    text: 'El mejor recurso que compré este año. Los combos y la estructura de las clases me salvaron la vida. Mis alumnos están fascinados con la variedad.'
  }, {
    name: 'Lucas M.',
    role: 'Dueño de Gimnasio',
    text: 'Implementar este sistema fue un antes y un después. Los profesores están más organizados y los alumnos no faltan a las clases. 100% recomendado.'
  }];
  return <section className="py-16 relative border-y border-white/5" data-id="element-54">
      <div className="absolute inset-0 bg-[#171717] opacity-80" data-id="element-55"></div>
      <div className="max-w-6xl mx-auto px-4 relative z-10" data-id="element-56">
        <div className="text-center mb-10" data-id="element-57">
          <h2 className="text-2xl md:text-4xl font-display font-bold mb-2 text-[#F5F5F5]" data-id="element-58">
            +1000 INSTRUCTORES YA LO USAN
          </h2>
          <p className="text-[#D4AF37] font-medium" data-id="element-59">
            Lo que dicen los profesionales (4.8/5)
          </p>
        </div>

        <Carousel items={testimonials} renderItem={t => <PremiumCard className="p-6 md:p-8 h-full flex flex-col" data-id="element-61">
              <div className="flex text-[#D4AF37] mb-4" data-id="element-62">
                {[...Array(5)].map((_, j) => <Star key={j} className="w-4 h-4" fill="currentColor" data-id="element-63" />)}
              </div>
              <p className="text-gray-300 mb-6 italic text-sm md:text-base flex-1" data-id="element-64">
                "{t.text}"
              </p>
              <div className="border-t border-white/10 pt-4 mt-auto" data-id="element-65">
                <p className="font-bold text-[#F5F5F5]" data-id="element-66">{t.name}</p>
                <p className="text-xs text-[#E53935]" data-id="element-67">{t.role}</p>
              </div>
            </PremiumCard>} data-id="element-60" />
      </div>
    </section>;
}
function ExploreInterior() {
  return <section className="py-20 px-4 relative" data-id="element-68">
      <div className="max-w-4xl mx-auto text-center relative z-10" data-id="element-69">
        <h2 className="text-3xl md:text-5xl font-display font-bold mb-4 text-[#F5F5F5]" data-id="element-70">
          EXPLORA EL INTERIOR DEL MEGAPACK
        </h2>
        <p className="text-gray-400 max-w-2xl mx-auto mb-12 text-lg" data-id="element-71">
          Conocé la calidad y practicidad de nuestras herramientas antes de
          armar tu próxima clase.
        </p>

        <div className="relative mx-auto max-w-sm" data-id="element-72">
          <div className="absolute -inset-4 bg-[#E53935] opacity-20 blur-2xl rounded-[32px]" data-id="element-73" />
          <video src={VIDEO_URL} autoPlay loop muted playsInline className="w-full rounded-[24px] border-2 border-[#E53935]/50 shadow-[0_0_30px_rgba(229,57,53,0.3)] relative z-10" data-id="element-74" />
        </div>

        <div className="mt-12" data-id="element-75">
          <ScrollButton text="Obtener acceso inmediato" data-id="element-76" />
        </div>
      </div>
    </section>;
}
function PainPoints() {
  const points = [{
    icon: AlertTriangle,
    text: 'Domingos a la noche: nervios y ansiedad por no saber qué clase dar lunes'
  }, {
    icon: Clock,
    text: 'Planificar después de otro día full es agotador y lleva horas que no recuperás'
  }, {
    icon: BookOpen,
    text: 'Clases repetidas, combos reciclados que aburren a tus alumnos'
  }, {
    icon: TrendingUp,
    text: 'Pérdida de alumnos sin saber exactamente por qué'
  }, {
    icon: Target,
    text: 'Sentís que tu marca profesional no crece y que improvisás más de lo que enseñás'
  }, {
    icon: Zap,
    text: 'Comparás tu trabajo con instructores de Instagram y sentís que quedás atrás'
  }];
  return <section className="py-20 px-4 relative border-t border-white/5" data-id="element-77">
      <div className="absolute inset-0 bg-[#171717] opacity-90" data-id="element-78"></div>
      <div className="max-w-5xl mx-auto relative z-10" data-id="element-79">
        <div className="text-center mb-16" data-id="element-80">
          <h2 className="text-3xl md:text-5xl font-display font-black mb-4 text-[#E53935] uppercase tracking-wide" data-id="element-81">
            ¿TE SUENA ESTO?
          </h2>
          <p className="text-xl text-gray-300" data-id="element-82">
            Si das clases de kickboxing, seguramente te pasó:
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-83">
          {points.map((p, i) => <PremiumCard key={i} className="p-6 flex flex-col items-center text-center" data-id="element-84">
              <div className="w-14 h-14 rounded-full bg-[#171717] border border-[#E53935]/30 flex items-center justify-center mb-6 shadow-[0_0_15px_rgba(229,57,53,0.2)]" data-id="element-85">
                <p.icon className="w-7 h-7 text-[#E53935]" data-id="element-86" />
              </div>
              <p className="text-gray-300 font-medium leading-relaxed" data-id="element-87">
                {p.text}
              </p>
            </PremiumCard>)}
        </div>

        <div className="text-center mt-16" data-id="element-88">
          <p className="text-2xl md:text-3xl font-display font-bold text-[#F5F5F5] mb-8" data-id="element-89">
            ¿Cuántos domingos más vas a perder buscando ideas?
          </p>
          <ScrollButton text="Ver ofertas" data-id="element-90" />
        </div>
      </div>
    </section>;
}
function TargetAudience() {
  const benefits = ['Clases completas y listas para dar, organizadas por niveles', 'Ahorrar tiempo recuperando 3-5 horas semanales', 'Sentirte seguro y profesional al planificar', 'Retener y atraer más alumnos', 'Diferenciarte con recursos prácticos, no teoría infinita'];
  return <section className="py-20 px-4 relative" data-id="element-91">
      <div className="max-w-4xl mx-auto relative z-10" data-id="element-92">
        <div className="text-center mb-12" data-id="element-93">
          <h2 className="text-3xl md:text-5xl font-display font-bold mb-4 text-[#F5F5F5]" data-id="element-94">
            ¿PARA QUIÉN ES?
          </h2>
          <p className="text-xl text-[#D4AF37]" data-id="element-95">
            Ideal para vos, instructor que quiere:
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-96">
          {benefits.map((b, i) => <PremiumCard key={i} className="p-6 flex items-start gap-4" data-id="element-97">
              <CheckCircle2 className="w-6 h-6 text-[#E53935] flex-shrink-0 mt-0.5" data-id="element-98" />
              <p className="text-gray-200 font-medium text-lg" data-id="element-99">{b}</p>
            </PremiumCard>)}
        </div>
      </div>
    </section>;
}
function ProductOverview() {
  return <section className="py-16 px-4 border-y border-white/5 relative bg-[#171717] text-center" data-id="element-100">
      <div className="max-w-5xl mx-auto relative z-10" data-id="element-101">
        <h2 className="text-2xl md:text-4xl font-display font-bold mb-12 text-[#F5F5F5]" data-id="element-102">
          El sistema más completo para instructores en Argentina
        </h2>

        <div className="flex flex-wrap justify-center gap-8 md:gap-20 mb-12" data-id="element-103">
          <div className="flex flex-col items-center" data-id="element-104">
            <span className="text-5xl md:text-6xl font-display font-black text-[#E53935] mb-2 drop-shadow-[0_0_10px_rgba(229,57,53,0.5)]" data-id="element-105">
              +350
            </span>
            <span className="text-sm text-[#D4AF37] uppercase tracking-widest font-bold" data-id="element-106">
              Páginas
            </span>
          </div>
          <div className="flex flex-col items-center" data-id="element-107">
            <span className="text-5xl md:text-6xl font-display font-black text-[#E53935] mb-2 drop-shadow-[0_0_10px_rgba(229,57,53,0.5)]" data-id="element-108">
              6
            </span>
            <span className="text-sm text-[#D4AF37] uppercase tracking-widest font-bold" data-id="element-109">
              Tomos
            </span>
          </div>
          <div className="flex flex-col items-center" data-id="element-110">
            <span className="text-5xl md:text-6xl font-display font-black text-[#E53935] mb-2 drop-shadow-[0_0_10px_rgba(229,57,53,0.5)]" data-id="element-111">
              6
            </span>
            <span className="text-sm text-[#D4AF37] uppercase tracking-widest font-bold" data-id="element-112">
              Bonos
            </span>
          </div>
        </div>
      </div>
    </section>;
}
function TomosSection() {
  const tomos = [{
    num: 1,
    title: 'Fundamentos y Estructura de Clase',
    desc: "El 'esqueleto' de una clase perfecta. Secuencia ideal: entrada en calor, bloque técnico, intensidad y vuelta a la calma.",
    img: 'https://images.unsplash.com/photo-1549719386-74dfcbf7dbed?auto=format&fit=crop&w=800&q=80'
  }, {
    num: 2,
    title: 'Nivel Iniciación (Blanco/Amarillo)',
    desc: 'Progresión técnica para nuevos: guardia, desplazamientos, jab-cross, patadas. 12 clases listas para captar alumnos.',
    img: 'https://images.unsplash.com/photo-1599058917212-d750089bc07e?auto=format&fit=crop&w=800&q=80'
  }, {
    num: 3,
    title: 'Nivel Intermedio (Naranja/Verde)',
    desc: 'Combos puño-pierna, trabajo con manoplas y saco, esquivas y contraataques. 12 clases para motivar y mostrar progreso.',
    img: 'https://images.unsplash.com/photo-1589829085413-56de8ae18c73?auto=format&fit=crop&w=800&q=80'
  }, {
    num: 4,
    title: 'Nivel Avanzado (Azul/Marrón)',
    desc: 'Combinaciones complejas, juegos de pies, low kicks, potencia y contraataque. 12 clases para retener a los más comprometidos.',
    img: 'https://images.unsplash.com/photo-1555597673-b21d5c935865?auto=format&fit=crop&w=800&q=80'
  }, {
    num: 5,
    title: 'Clases Temáticas de Alto Impacto',
    desc: 'HIIT-Kickboxing, combos exclusivos, defensa personal y resistencia. Rompé la rutina y atraé alumnos nuevos.',
    img: 'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?auto=format&fit=crop&w=800&q=80'
  }, {
    num: 6,
    title: 'Planificador Anual + Seguimiento',
    desc: 'Calendario para no repetir contenido. Plantillas de progreso individual, exámenes de cinturón y fichas técnicas.',
    img: 'https://images.unsplash.com/photo-1506784983877-45594efa4cbe?auto=format&fit=crop&w=800&q=80'
  }];
  return <section className="py-20 relative" data-id="element-113">
      <div className="max-w-6xl mx-auto px-4" data-id="element-114">
        <h2 className="text-3xl md:text-5xl font-display font-black text-center mb-12 text-[#F5F5F5] uppercase tracking-wide" data-id="element-115">
          LOS 6 TOMOS
        </h2>

        <Carousel items={tomos} renderItem={t => <PremiumCard className="h-full flex flex-col p-0" data-id="element-117">
              <div className="h-[30%] relative overflow-hidden border-b border-[#E53935]/20" data-id="element-118">
                <img src={t.img} alt={t.title} className="w-full h-full object-cover" data-id="element-119" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1F1F1F] to-transparent" data-id="element-120" />
                <div className="absolute top-3 left-3 bg-[#E53935] text-white w-8 h-8 flex items-center justify-center rounded-full font-bold shadow-lg" data-id="element-121">
                  {t.num}
                </div>
              </div>
              <div className="p-6 h-[70%] flex flex-col" data-id="element-122">
                <h3 className="text-xl font-display font-bold mb-3 text-[#D4AF37] leading-tight" data-id="element-123">
                  {t.title}
                </h3>
                <p className="text-gray-300 text-sm leading-relaxed flex-1" data-id="element-124">
                  {t.desc}
                </p>
              </div>
            </PremiumCard>} data-id="element-116" />
      </div>
    </section>;
}
function BonusesSection() {
  const bonuses = [{
    title: 'Banco de 100 Combos',
    desc: 'Puño-pierna listos para combinar.',
    img: 'https://images.unsplash.com/photo-1517836357463-d25dfeac3438?auto=format&fit=crop&w=800&q=80'
  }, {
    title: 'Examen de Cinturón',
    desc: 'Plantilla editable con criterios claros.',
    img: 'https://images.unsplash.com/photo-1552674605-db6ffd4facb5?auto=format&fit=crop&w=800&q=80'
  }, {
    title: 'Guion de Clase de Prueba',
    desc: 'Estructura para convertir visitantes.',
    img: 'https://images.unsplash.com/photo-1576678927484-cc907957088c?auto=format&fit=crop&w=800&q=80'
  }, {
    title: 'Calendario Redes (30 días)',
    desc: 'Ideas de reels y posts para Instagram/FB.',
    img: 'https://images.unsplash.com/photo-1611162617474-5b21e879e113?auto=format&fit=crop&w=800&q=80'
  }, {
    title: 'Checklist de Seguridad',
    desc: 'Prevención de lesiones para cada bloque.',
    img: 'https://images.unsplash.com/photo-1586281380349-632531db7ed4?auto=format&fit=crop&w=800&q=80'
  }, {
    title: 'Glosario Bilingüe',
    desc: 'Términos en Español/Inglés.',
    img: 'https://images.unsplash.com/photo-1456513080510-7bf3a84b82f8?auto=format&fit=crop&w=800&q=80'
  }];
  return <section className="py-20 px-4 relative bg-[#171717] border-y border-white/5" data-id="element-125">
      <div className="max-w-5xl mx-auto" data-id="element-126">
        <div className="text-center mb-16" data-id="element-127">
          <h2 className="text-3xl md:text-5xl font-display font-black mb-4 text-[#D4AF37] uppercase" data-id="element-128">
            6 Bonos Únicos
          </h2>
          <p className="text-xl text-gray-400 font-medium" data-id="element-129">
            Alto Valor Percibido (Valor estimado: $60.000 ARS)
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-130">
          {bonuses.map((b, i) => <PremiumCard key={i} className="p-0 flex flex-col h-full" data-id="element-131">
              <div className="h-32 relative overflow-hidden border-b border-[#E53935]/20" data-id="element-132">
                <img src={b.img} alt={b.title} className="w-full h-full object-cover" data-id="element-133" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1F1F1F] to-transparent" data-id="element-134" />
                <div className="absolute top-3 right-3 bg-[#D4AF37] text-[#171717] text-xs font-bold px-2 py-1 rounded uppercase shadow-md" data-id="element-135">
                  Bono {i + 1}
                </div>
              </div>
              <div className="p-6 flex-1 flex flex-col" data-id="element-136">
                <h3 className="text-lg font-display font-bold text-[#F5F5F5] mb-2" data-id="element-137">
                  {b.title}
                </h3>
                <p className="text-gray-400 text-sm" data-id="element-138">{b.desc}</p>
              </div>
            </PremiumCard>)}
        </div>

        <div className="text-center mt-12" data-id="element-139">
          <ScrollButton text="Acceder al MegaPack" data-id="element-140" />
        </div>
      </div>
    </section>;
}
function GuaranteeBanner() {
  return <section className="py-10 bg-[#1F1F1F] border-y border-[#D4AF37]/30" data-id="element-141">
      <div className="max-w-4xl mx-auto px-4 text-center flex flex-col items-center" data-id="element-142">
        <ShieldCheck className="w-14 h-14 text-[#D4AF37] mb-4 drop-shadow-[0_0_10px_rgba(212,175,55,0.5)]" data-id="element-143" />
        <h2 className="text-2xl md:text-3xl font-display font-black text-[#F5F5F5] mb-2 uppercase tracking-wide" data-id="element-144">
          7 DÍAS GARANTÍA TOTAL
        </h2>
        <p className="text-base md:text-lg text-gray-400 font-medium" data-id="element-145">
          Probalo y si no te sirve, te devolvemos el dinero sin preguntas.
        </p>
      </div>
    </section>;
}
function CostOfNotHaving() {
  const sinMegapack = ['Domingos con ansiedad por no saber qué dar', 'Clases repetidas que aburren a los alumnos', 'Horas perdidas planificando después del trabajo', 'Alumnos que abandonan sin que sepas por qué', 'Improvisación constante en tus clases', 'Sentís que te quedás atrás de la competencia'];
  const conMegapack = ['Planificación mensual lista en 15 minutos', 'Clases variadas, dinámicas y profesionales', 'Recuperás 3-5 horas de tu tiempo libre semanal', 'Mayor retención y alumnos más motivados', 'Estructura probada que te da seguridad al enseñar', 'Te posicionás como un referente en tu gimnasio'];
  const [pos, setPos] = useState(50);
  return <section className="py-24 px-4 overflow-hidden relative" data-id="element-146">
      <div className="max-w-5xl mx-auto relative z-10" data-id="element-147">
        <div className="text-center mb-12" data-id="element-148">
          <h2 className="text-3xl md:text-5xl font-display font-black text-[#F5F5F5] uppercase" data-id="element-149">
            ¿Costo de NO tenerlo?
          </h2>
          <p className="text-gray-400 mt-4 text-lg" data-id="element-150">
            Deslizá la barra para comparar tu día a día{' '}
            <span className="font-bold text-[#E53935]" data-id="element-151">sin</span> y{' '}
            <span className="font-bold text-[#D4AF37]" data-id="element-152">con</span> el MegaPack.
          </p>
        </div>

        <div className="relative w-full rounded-[24px] overflow-hidden border border-white/10 shadow-[0_0_30px_rgba(0,0,0,0.5)] select-none bg-[#171717]" data-id="element-153">
          {/* CON MEGAPACK (base / right) */}
          <div className="p-6 md:p-10 pl-[55%] md:pl-[55%]" data-id="element-154">
            <div className="flex items-center gap-2 text-[#D4AF37] font-display font-bold text-xl md:text-2xl mb-6 pb-4 border-b border-white/10" data-id="element-155">
              <CheckCircle2 className="w-6 h-6 md:w-8 md:h-8" data-id="element-156" /> CON el MegaPack
            </div>
            <ul className="space-y-4" data-id="element-157">
              {conMegapack.map((item, i) => <li key={i} className="flex items-start gap-3 text-gray-300 text-sm md:text-base font-medium" data-id="element-158">
                  <CheckCircle2 className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" data-id="element-159" />
                  {item}
                </li>)}
            </ul>
          </div>

          {/* SIN MEGAPACK (overlay / left) */}
          <div className="absolute inset-0 bg-[#1F1F1F] p-6 md:p-10 overflow-hidden border-r border-[#E53935]/50" style={{
          clipPath: `inset(0 ${100 - pos}% 0 0)`
        }} data-id="element-160">
            <div className="flex items-center gap-2 text-[#E53935] font-display font-bold text-xl md:text-2xl mb-6 pb-4 border-b border-white/10 whitespace-nowrap w-[200%]" data-id="element-161">
              <XCircle className="w-6 h-6 md:w-8 md:h-8" data-id="element-162" /> SIN el MegaPack
            </div>
            <ul className="space-y-4 w-[200%]" data-id="element-163">
              {sinMegapack.map((item, i) => <li key={i} className="flex items-start gap-3 text-gray-400 text-sm md:text-base font-medium whitespace-nowrap" data-id="element-164">
                  <XCircle className="w-5 h-5 text-[#E53935] flex-shrink-0 mt-0.5" data-id="element-165" />
                  {item}
                </li>)}
            </ul>
          </div>

          {/* Divider bar + handle */}
          <div className="absolute top-0 bottom-0 w-1 bg-[#E53935] shadow-[0_0_15px_rgba(229,57,53,0.8)] pointer-events-none" style={{
          left: `${pos}%`,
          transform: 'translateX(-50%)'
        }} data-id="element-166">
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 rounded-full bg-[#171717] border-2 border-[#E53935] flex items-center justify-center text-[#F5F5F5] shadow-lg" data-id="element-167">
              <ChevronLeft className="w-4 h-4 md:w-5 md:h-5 -mr-1" data-id="element-168" />
              <ChevronRight className="w-4 h-4 md:w-5 md:h-5 -ml-1" data-id="element-169" />
            </div>
          </div>

          <input type="range" min={0} max={100} value={pos} onChange={e => setPos(Number(e.target.value))} aria-label="Comparar sin y con el MegaPack" className="absolute inset-0 w-full h-full opacity-0 cursor-ew-resize" data-id="element-170" />
        </div>
      </div>
    </section>;
}
function PricingSection({
  id
}: {
  id: string;
}) {
  return <section className="py-24 px-4 relative bg-[#171717] border-y border-white/5" id={id} data-id="element-171">
      <div className="text-center mb-16" data-id="element-172">
        <h2 className="text-4xl md:text-6xl font-display font-black mb-4 text-[#F5F5F5] uppercase tracking-tight" data-id="element-173">
          Elegí tu Megapack
        </h2>
        <p className="text-xl text-[#D4AF37] font-medium" data-id="element-174">
          Acceso inmediato y de por vida
        </p>
      </div>

      <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-8 md:gap-12 items-stretch" data-id="element-175">
        {/* Offer 1 - Normal */}
        <PremiumCard className="p-8 md:p-10 flex flex-col h-full border-[#1F1F1F] hover:border-[#E53935]/30" data-id="element-176">
          <div className="text-center mb-8 pb-8 border-b border-white/10" data-id="element-177">
            <h3 className="text-2xl font-display font-bold text-[#F5F5F5] mb-3" data-id="element-178">
              MegaPack Kickboxing Resuelto
            </h3>
            <div className="text-5xl font-display font-black text-[#F5F5F5] my-6" data-id="element-179">
              $20.990
            </div>
            <p className="text-sm text-gray-400 font-medium uppercase tracking-wider" data-id="element-180">
              Pago único
            </p>
          </div>

          <ul className="space-y-5 mb-10 flex-1" data-id="element-181">
            <li className="flex items-start gap-3 text-gray-300 text-base" data-id="element-182">
              <CheckCircle2 className="w-6 h-6 text-[#E53935] flex-shrink-0" data-id="element-183" />{' '}
              6 Tomos Principales
            </li>
            <li className="flex items-start gap-3 text-gray-300 text-base" data-id="element-184">
              <CheckCircle2 className="w-6 h-6 text-[#E53935] flex-shrink-0" data-id="element-185" />{' '}
              6 Bonos Gratuitos
            </li>
            <li className="flex items-start gap-3 text-gray-300 text-base" data-id="element-186">
              <CheckCircle2 className="w-6 h-6 text-[#E53935] flex-shrink-0" data-id="element-187" />{' '}
              Acceso Inmediato PDF
            </li>
            <li className="flex items-start gap-3 text-gray-300 text-base" data-id="element-188">
              <CheckCircle2 className="w-6 h-6 text-[#E53935] flex-shrink-0" data-id="element-189" />{' '}
              Garantía 7 días
            </li>
          </ul>

          <a href={LINK_NORMAL} className="block w-full text-center bg-[#171717] border border-[#E53935]/50 text-[#F5F5F5] hover:bg-[#E53935]/10 font-display font-bold py-4 rounded-full transition-colors" data-id="element-190">
            COMPRAR AHORA
          </a>
        </PremiumCard>

        {/* Offer 2 - PRO */}
        <PremiumCard className="p-8 md:p-10 flex flex-col h-full relative border-[#D4AF37]/50 shadow-[0_0_30px_rgba(212,175,55,0.15)] transform md:-translate-y-4" data-id="element-191">
          <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-gradient-to-r from-[#D4AF37] to-[#f1c40f] text-[#171717] text-sm font-black px-6 py-1.5 rounded-full uppercase tracking-widest shadow-lg" data-id="element-192">
            ⭐ MÁS ELEGIDO
          </div>

          <div className="text-center mb-8 pb-8 border-b border-white/10" data-id="element-193">
            <h3 className="text-2xl font-display font-bold text-white mb-4 flex flex-col items-center gap-2" data-id="element-194">
              <span data-id="element-195">MegaPack PRO</span>
              <span className="bg-[#E53935] text-white px-3 py-1 rounded-md text-sm uppercase tracking-wider" data-id="element-196">
                + Muay Thai
              </span>
            </h3>
            <div className="text-5xl font-display font-black text-[#D4AF37] my-6 drop-shadow-[0_0_10px_rgba(212,175,55,0.3)]" data-id="element-197">
              $27.990
            </div>
            <p className="text-sm text-gray-400 font-medium uppercase tracking-wider" data-id="element-198">
              Pago único
            </p>
          </div>

          <ul className="space-y-5 mb-10 flex-1" data-id="element-199">
            <li className="flex items-start gap-3 text-gray-200 text-base" data-id="element-200">
              <CheckCircle2 className="w-6 h-6 text-[#D4AF37] flex-shrink-0" data-id="element-201" />{' '}
              <strong data-id="element-202">
                Todo el contenido del MegaPack Kickboxing Resuelto
              </strong>
            </li>
            <li className="flex items-start gap-3 text-gray-200 text-base" data-id="element-203">
              <CheckCircle2 className="w-6 h-6 text-[#D4AF37] flex-shrink-0" data-id="element-204" />{' '}
              <strong data-id="element-205">+ 4 Tomos Premium Adicionales</strong>
            </li>
            <li className="flex items-start gap-3 text-gray-200 text-base" data-id="element-206">
              <CheckCircle2 className="w-6 h-6 text-[#D4AF37] flex-shrink-0" data-id="element-207" />{' '}
              Expansión Muay Thai (Clinch/Rodillazos)
            </li>
            <li className="flex items-start gap-3 text-gray-200 text-base" data-id="element-208">
              <CheckCircle2 className="w-6 h-6 text-[#D4AF37] flex-shrink-0" data-id="element-209" />{' '}
              Estrategias de Retención y Marketing
            </li>
          </ul>

          <a href={LINK_PRO} className="block w-full text-center bg-gradient-to-r from-[#E53935] to-[#b71c1c] text-white font-display font-bold py-4 rounded-full transition-all hover:scale-105 shadow-[0_0_20px_rgba(229,57,53,0.5)]" data-id="element-210">
            🔥 COMPRAR AHORA
          </a>
        </PremiumCard>
      </div>

      <div className="text-center mt-12 text-sm text-gray-500 font-medium uppercase tracking-widest" data-id="element-211">
        PAGO SEGURO — DESCARGA AL INSTANTE — GARANTÍA TOTAL 7 DÍAS
      </div>
    </section>;
}
function UpsellDetails() {
  const upsellTomos = [{
    num: 7,
    title: 'Expansión Clinch y Rodillazos (Técnicas de Muay Thai aplicadas)',
    desc: 'Aprendé a incorporar técnicas de clinch, rodillazos, codos y derribos, adaptados para clase grupales seguras, que amplían el repertorio técnico más allá del kickboxing tradicional, sin necesidad de ser cinturón negro en Muay Thai. Diferenciación directa con alto impacto técnico y académico.',
    img: 'https://images.unsplash.com/photo-1591117207239-788bf8de6c3b?auto=format&fit=crop&w=800&q=80'
  }, {
    num: 8,
    title: 'El Instructor Imán: Retención y Crecimiento de tu Cartera de Alumnos',
    desc: 'Usá estrategias probadas para construir una comunidad sólida: diseño de sistemas de niveles y paquetes que generan compromiso a mediano y largo plazo, técnicas para mejorar la comunicación y fidelización, consejos para posicionarte como referente y negociar mejor turnos y tarifas.',
    img: 'https://images.unsplash.com/photo-1599058917212-d750089bc07e?auto=format&fit=crop&w=800&q=80'
  }, {
    num: 9,
    title: 'Entrenamiento Funcional y Condicionamiento para Kickboxing y Muay Thai',
    desc: 'Rutinas específicas para optimizar la condición física de tus alumnos, enfocadas en resistencia, potencia, movilidad y prevención de lesiones. Incluye planillas de trabajo y progresiones para distintos perfiles (novatos, intermedios y avanzados).',
    img: 'https://images.unsplash.com/photo-1534438327276-14e5300c3a48?auto=format&fit=crop&w=800&q=80'
  }, {
    num: 10,
    title: 'Comunicación y Marketing para Instructores de Artes Marciales',
    desc: 'Todo lo que necesitás para fortalecer tu marca personal en redes sociales, crear contenido que engancha y convierte, y manejar promociones que atraen alumnos sin que sientas que estás “vendiendo” de más. Plan de 90 días de contenido listo para usar.',
    img: 'https://images.unsplash.com/photo-1611162616475-46b635cb6868?auto=format&fit=crop&w=800&q=80'
  }];
  return <section className="py-24 relative overflow-hidden" data-id="element-212">
      <div className="absolute inset-0 bg-[#E53935] opacity-5 blur-[150px] rounded-full pointer-events-none" data-id="element-213" />
      <div className="max-w-6xl mx-auto px-4 relative z-10" data-id="element-214">
        <div className="text-center mb-16" data-id="element-215">
          <div className="inline-block bg-[#1F1F1F] border border-[#E53935]/50 text-[#F5F5F5] text-sm font-bold px-6 py-2 rounded-full mb-6 uppercase tracking-wider shadow-[0_0_15px_rgba(229,57,53,0.2)]" data-id="element-216">
            🔥 OFERTA 2 — MegaPack Kickboxing PRO + Expansión Muay Thai
          </div>
          <h2 className="text-3xl md:text-5xl font-display font-black text-[#D4AF37] mb-4 uppercase" data-id="element-217">
            ¿QUÉ AGREGA EL MEGAPACK AVANZADO?
          </h2>
          <p className="text-2xl font-display font-bold text-[#F5F5F5] mb-6" data-id="element-218">
            Precio: $27.990 ARS
          </p>
          <p className="text-gray-300 max-w-3xl mx-auto text-base md:text-lg leading-relaxed mb-6" data-id="element-219">
            Incluye todo lo de la Oferta 1 (6 Tomos + 6 Bonos), más un paquete
            exclusivo de 4 tomos adicionales y recursos premium que te
            convierten en el instructor más completo, solicitado y diferenciado
            del gimnasio.
          </p>
          <p className="text-[#E53935] font-bold uppercase text-sm tracking-widest" data-id="element-220">
            Contenido exclusivo adicional (4 tomos + 3 bonos extra):
          </p>
        </div>

        <Carousel items={upsellTomos} renderItem={t => <PremiumCard className="h-full flex flex-col p-0 border-[#D4AF37]/30" data-id="element-222">
              <div className="h-[30%] relative overflow-hidden border-b border-white/10" data-id="element-223">
                <img src={t.img} alt={t.title} className="w-full h-full object-cover" data-id="element-224" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1F1F1F] to-transparent" data-id="element-225" />
                <div className="absolute top-3 left-3 bg-[#D4AF37] text-[#171717] font-bold px-3 py-1 rounded text-xs uppercase shadow-md" data-id="element-226">
                  Tomo {t.num}
                </div>
              </div>
              <div className="p-6 h-[70%] flex flex-col" data-id="element-227">
                <h3 className="text-lg font-display font-bold mb-3 text-[#F5F5F5] leading-tight" data-id="element-228">
                  {t.title}
                </h3>
                <p className="text-gray-400 text-sm leading-relaxed flex-1" data-id="element-229">
                  {t.desc}
                </p>
              </div>
            </PremiumCard>} data-id="element-221" />

        <div className="text-center mt-16" data-id="element-230">
          <ScrollButton text="🔥 QUIERO EL MEGAPACK PRO — $27.990" data-id="element-231" />
        </div>
      </div>
    </section>;
}
function FAQSection() {
  const faqs = [{
    q: '¿Recibo material inmediatamente?',
    a: 'Sí, al completar tu compra te llega automáticamente el link de descarga a tu email para que accedas al instante.'
  }, {
    q: '¿Sirve si recién empiezo como instructor?',
    a: 'Claro, está pensado para todos los niveles, especialmente para quienes quieren organizarse mejor desde el día uno y no improvisar.'
  }, {
    q: '¿Los bonos están incluidos en las dos ofertas?',
    a: 'Sí, los 6 bonos gratuitos se incluyen sin costo adicional en cualquiera de las dos opciones que elijas.'
  }, {
    q: '¿En qué formato viene el MegaPack?',
    a: 'Es un producto 100% digital en formato PDF, optimizado para leer en celular, tablet o imprimir si lo preferís.'
  }, {
    q: '¿Cómo funciona la garantía?',
    a: 'Tenés 7 días para revisar todo el material. Si sentís que no te aporta valor, nos escribís y te devolvemos el 100% de tu dinero sin preguntas.'
  }];
  const [open, setOpen] = useState<number | null>(0);
  return <section className="py-24 bg-[#171717] px-4 border-y border-white/5" data-id="element-232">
      <div className="max-w-3xl mx-auto" data-id="element-233">
        <h2 className="text-3xl md:text-5xl font-display font-black text-center mb-12 text-[#F5F5F5] uppercase" data-id="element-234">
          Preguntas Frecuentes
        </h2>

        <div className="space-y-4" data-id="element-235">
          {faqs.map((faq, i) => <PremiumCard key={i} className="p-0 rounded-2xl" data-id="element-236">
              <button className="w-full px-6 py-5 text-left flex justify-between items-center font-bold text-lg text-[#F5F5F5]" onClick={() => setOpen(open === i ? null : i)} data-id="element-237">
                {faq.q}
                <ChevronDown className={`w-5 h-5 transition-transform ${open === i ? 'rotate-180 text-[#E53935]' : 'text-gray-500'}`} data-id="element-238" />
              </button>
              <AnimatePresence data-id="element-239">
                {open === i && <motion.div initial={{
              height: 0,
              opacity: 0
            }} animate={{
              height: 'auto',
              opacity: 1
            }} exit={{
              height: 0,
              opacity: 0
            }} className="px-6 pb-5 text-gray-400" data-id="element-240">
                    {faq.a}
                  </motion.div>}
              </AnimatePresence>
            </PremiumCard>)}
        </div>
      </div>
    </section>;
}
function UrgencySection() {
  return <section className="py-16 bg-[#1F1F1F] border-y border-[#E53935]/30 text-center px-4 relative overflow-hidden" data-id="element-241">
      <div className="absolute inset-0 bg-[#E53935] opacity-5 blur-[100px] rounded-full pointer-events-none" data-id="element-242" />
      <div className="max-w-2xl mx-auto relative z-10" data-id="element-243">
        <div className="inline-flex items-center justify-center gap-2 bg-[#E53935]/10 border border-[#E53935]/30 text-[#E53935] px-5 py-2 rounded-full font-bold text-sm mb-6 uppercase tracking-wider" data-id="element-244">
          <AlertTriangle className="w-4 h-4" data-id="element-245" /> 🔥 Última oportunidad
        </div>
        <h2 className="text-3xl md:text-4xl font-display font-black text-[#F5F5F5] mb-4 uppercase" data-id="element-246">
          No dejes que se acaben los cupos
        </h2>
        <div className="my-8 bg-[#171717] p-6 rounded-2xl border border-white/5" data-id="element-247">
          <div className="flex justify-between text-sm font-bold text-[#E53935] mb-3 uppercase tracking-wider" data-id="element-248">
            <span data-id="element-249">Cupos restantes</span>
            <span data-id="element-250">68% TOMADOS</span>
          </div>
          <div className="w-full bg-[#1F1F1F] rounded-full h-4 border border-white/5 overflow-hidden" data-id="element-251">
            <div className="bg-gradient-to-r from-[#E53935] to-[#b71c1c] h-full rounded-full relative" style={{
            width: '68%'
          }} data-id="element-252">
              <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/diagonal-stripes.png')] opacity-20" data-id="element-253"></div>
            </div>
          </div>
        </div>
        <p className="text-gray-400 font-medium text-lg" data-id="element-254">
          Cuando se acaben los cupos el precio sube y{' '}
          <strong className="text-[#E53935]" data-id="element-255">NO vuelve a bajar.</strong>
        </p>
      </div>
    </section>;
}
function Footer() {
  return <footer className="bg-[#171717] py-12 border-t border-white/5 text-center px-4" data-id="element-256">
      <div className="max-w-4xl mx-auto" data-id="element-257">
        <p className="text-gray-500 mb-4 text-sm font-medium" data-id="element-258">
          Contacto: soporteacademiaonline.ar@gmail.com
        </p>
        <p className="text-gray-600 text-xs" data-id="element-259">
          © MegaPack Kickboxing Resuelto™ 2026 — Todos los derechos
          reservados.
        </p>
      </div>
    </footer>;
}
function RecentPurchasePopup() {
  const [isVisible, setIsVisible] = useState(false);
  const [purchaseData, setPurchaseData] = useState({
    name: '',
    city: '',
    role: ''
  });
  const names = ['Martín', 'Lucía', 'Facundo', 'Camila', 'Agustín', 'Valentina', 'Joaquín', 'Sofía', 'Tomás', 'Julieta', 'Nicolás', 'Martina'];
  const cities = ['CABA', 'Córdoba', 'Rosario', 'Mendoza', 'La Plata', 'Mar del Plata', 'Salta', 'San Miguel de Tucumán', 'Santa Fe', 'Neuquén'];
  const roles = ['Entrenador de Kickboxing', 'Profesor de Artes Marciales', 'Dueño de Gimnasio', 'Instructor', 'Entrenador Personal', 'Practicante Avanzado'];
  useEffect(() => {
    const showPopup = () => {
      const randomName = names[Math.floor(Math.random() * names.length)];
      const randomCity = cities[Math.floor(Math.random() * cities.length)];
      const randomRole = roles[Math.floor(Math.random() * roles.length)];
      setPurchaseData({
        name: randomName,
        city: randomCity,
        role: randomRole
      });
      setIsVisible(true);
      setTimeout(() => {
        setIsVisible(false);
      }, 5000); // Show for 5 seconds
    };
    // Initial delay before first popup
    const initialTimer = setTimeout(showPopup, 10000);
    // Then repeat every 25 seconds
    const intervalTimer = setInterval(showPopup, 25000);
    return () => {
      clearTimeout(initialTimer);
      clearInterval(intervalTimer);
    };
  }, []);
  return <AnimatePresence data-id="element-260">
      {isVisible && <motion.div initial={{
      opacity: 0,
      y: 50,
      x: -20
    }} animate={{
      opacity: 1,
      y: 0,
      x: 0
    }} exit={{
      opacity: 0,
      y: 20,
      scale: 0.95
    }} transition={{
      duration: 0.4,
      ease: 'easeOut'
    }} className="fixed bottom-6 left-6 z-50 max-w-sm bg-[#1F1F1F] border border-[#E53935]/40 rounded-2xl p-4 shadow-[0_8px_30px_rgba(229,57,53,0.2)] flex items-start gap-4" data-id="element-261">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#E53935] to-[#b71c1c] flex items-center justify-center flex-shrink-0 shadow-[0_0_10px_rgba(229,57,53,0.4)]" data-id="element-262">
            <CheckCircle2 className="w-6 h-6 text-white" data-id="element-263" />
          </div>
          <div data-id="element-264">
            <p className="text-sm text-gray-300 mb-1" data-id="element-265">
              <span className="font-bold text-[#F5F5F5]" data-id="element-266">
                {purchaseData.name}
              </span>{' '}
              de {purchaseData.city}
            </p>
            <p className="text-xs text-[#D4AF37] font-medium mb-1" data-id="element-267">
              {purchaseData.role}
            </p>
            <p className="text-xs text-gray-400" data-id="element-268">
              Acaba de comprar el MegaPack PRO
            </p>
          </div>
        </motion.div>}
    </AnimatePresence>;
}